import {
	applyMiddleware,
	getDefaultMiddleware,
	createStore,
	compose,
	StoreEnhancer,
	Reducer,
	AnyAction,
	PreloadedState,
} from "@reduxjs/toolkit";
import { install as installReduxLoop, isLoop, ListCmd, ActionCmd } from "redux-loop";
import { remoteReduxServerEnhancer } from "../../../common/redux";
import devtoolsEnhancer from "../common/devtoolsEnhancer";
import { getSingleton, singletonFactory } from "../../../common/Singleton";
import { RemoteReduxServerAddress } from "../../../common/redux/types";

export type ActionToDispatch = ActionCmd<AnyAction>["actionToDispatch"] & {
	payload?: Record<string, any>;
};

/**
 * Lazily creates a store singleton that is compatible with remote redux.
 *
 * Note: the server address is also used as the singleton name.
 *
 * @params server - The remote redux server address which must be listed in /src/common/redux/types
 * @params initialState - The initial state of the store
 * @params getReducer - A function that returns a reducer for the store (see createReducer())
 */
export const createRemoteReduxStore = <StoreType>(
	server: RemoteReduxServerAddress,
	initialState: PreloadedState<StoreType>,
	getReducer: () => Reducer<StoreType, AnyAction>
) =>
	singletonFactory(server, () => {
		/**
		 * Our proprietary createReducer() requires that redux-loops middleware be installed.
		 *
		 * Even though we're using our own version of RTK's createReducer we still use RTK's middleware because
		 * it provides some nice runtime invariants that will print console errors when bad things happen in the store.
		 *
		 * The ordering here is critical. The remoteReduxServerEnhancer must be a parent of installReduxLoop
		 * (it must come first in the compose order, which means it's the last item to act)
		 */
		const enhancer = compose(
			remoteReduxServerEnhancer({ server }),
			installReduxLoop(),
			applyMiddleware(...getDefaultMiddleware()),
			devtoolsEnhancer as StoreEnhancer
		);

		return createStore(getReducer(), initialState, enhancer);
	});

export const getRemoteReduxStore = <StoreType>(server: RemoteReduxServerAddress) => getSingleton<StoreType>(server);

/**
 * This returns helper functions for running unit tests against a store, all with type safety for your store:
 *
 * reducer() - A function that be used to run an action against your store's reducers and which returns a simple state. Redux-loops (effects) are removed in order to simplify the syntax.
 *
 * effectsReducer() - The same reducer except that the redux-loop commands (Cmd) are retained as a second parameter. This allows you to examine which effects will be triggered.
 *
 * makeAction() - A convenience method for creating actions, though generally you should import actions directly instead of creating them from scratch.
 *
 * getActions() - Returns an array of actions (follow-on dispatches) given a Cmd effects object returned by redux-loop (or an empty array.)
 *
 * getRuns() - Returns an array of run commands given a Cmd effects object returned by redux loop (or any empty array.) Each object will have a func and
 * args which can be asserted against.
 *
 * getEffects() - Returns an array of all redux-loop effects (or any empty array).
 *
 * @param getReducerFunction A function that returns a reducer for your store. Our service's stores each export a getReducer() function which can be used.
 * @returns Helper functions for unit testing a redux store with redux-loop
 */
export const makeTestHelpers = <StoreType>(getReducerFunction: () => Reducer<StoreType, AnyAction>) => {
	const rawReducer = getReducerFunction();

	const reducer = (previousState: StoreType, action: AnyAction): StoreType => {
		const result = rawReducer(previousState, action);
		if (isLoop(result)) {
			return result[0];
		} else {
			return result;
		}
	};

	const makeAction = (action: string, fsa: any): { type: string; payload: any } => {
		return {
			type: action,
			payload: fsa,
		};
	};

	const getActions = (cmds?: ListCmd): ActionToDispatch[] =>
		(cmds?.cmds ?? []).flatMap((cmd) => (cmd.type === "ACTION" ? [cmd.actionToDispatch] : []));
	/**
	 *
	 * @param cmds Cmds returned from a call to effectsReducer()
	 * @returns An array of { name, func, args } tuples that contain the function call and arguments for each scheduled effect.
	 * `name` is the name of the function, derived from calling func.name.
	 * The array will be empty if there are no function effects. Use the returned results in unit test assertions.
	 *
	 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/name
	 */
	const getRuns = (cmds?: ListCmd) =>
		(cmds?.cmds ?? [])
			.flatMap((cmd) => (cmd.type === "RUN" ? [cmd] : []))
			.map((cmd) => {
				return {
					name: cmd.func.name,
					func: cmd.func,
					args: cmd.args,
				};
			});
	const getEffects = (cmds?: ListCmd) => cmds?.cmds ?? [];

	/**
	 * Runs the reducer for a given action, returning a tuple of new state and redux-loop commands (effects). If a mockActions
	 * record is passed, then any run effects with a matching function will be run, therefore further updating the state.
	 *
	 * @param currentState - The current store's state
	 * @param action - The action to run
	 * @param mockActions - A record of mock actions, by function name, to run for any redux-loops (cmds) that are triggered
	 * @returns A tuple of the new state and the redux-loops
	 */
	const effectsReducer = (
		currentState: StoreType,
		action: AnyAction,
		mockActions?: Record<string, Function>
	): [StoreType, ListCmd] => {
		const nextState = rawReducer(currentState, action) as any as [StoreType, ListCmd];
		if (!mockActions) return nextState;

		// Run mockActions for any run effects that were returned, using reduce() to run a sequence of actions
		const runs = getRuns(nextState[1]);
		const state = runs.reduce((acc, run) => {
			const mockAction = mockActions[run.name];
			if (!mockAction) return acc;
			const newAction = mockAction.apply(null, run.args);
			return rawReducer(acc, newAction)[0];
		}, nextState[0]);

		return [state, nextState[1]];
	};

	return { reducer, effectsReducer, makeAction, getActions, getRuns, getEffects };
};
